<template>
  <div class="forum-container">
    <!-- 顶部操作栏 -->
    <div class="forum-header">
      <div class="forum-header-left">
        <h2 class="forum-title">事项讨论区</h2>
        <div class="forum-tabs">
          <div
            class="forum-tab"
            :class="{ active: activeTab === 'todo' }"
            @click="switchTab('todo')"
          >
            <i class="el-icon-document"></i>
            待办事项
            <span class="tab-count">{{ total }}</span>
          </div>
          <div
            class="forum-tab"
            :class="{ active: activeTab === 'image' }"
            @click="switchTab('image')"
          >
            <i class="el-icon-picture"></i>
            图片
            <span class="tab-count">{{ imageList.length }}</span>
          </div>
        </div>
      </div>
      <div class="forum-header-right">
        <el-button v-if="activeTab === 'todo' && operatePermission()" type="primary" plain size="small" @click="handleAdd">
          <i class="el-icon-edit"></i>
          添加事项
        </el-button>
        <el-button v-if="activeTab === 'image'" type="primary" size="small" @click="openImageDialog">
          <i class="el-icon-upload"></i>
          添加图片
        </el-button>
      </div>
    </div>
    <!-- 事项 Tab 容器 -->
    <div v-if="activeTab === 'todo'" class="forum-todo-wrapper">
    <!-- 搜索区域 (仅事项 tab 显示) -->
    <div class="forum-search">
      <el-input
        v-model="searchForm.keyword"
        placeholder="搜索事项标题或内容..."
        clearable
        class="search-input"
        @keyup.enter.native="handleSearch"
        prefix-icon="el-icon-search"
      >

      </el-input>
      <el-select v-model="searchForm.department" placeholder="部门筛选" clearable filterable class="dept-select" @change="handleSearch">
        <el-option
          v-for="dept in departments"
          :key="dept.id"
          :label="dept.name"
          :value="dept.name"
        ></el-option>
      </el-select>
      <el-select v-model="searchForm.status" placeholder="状态筛选" clearable filterable class="filter-select" @change="handleSearch">
        <el-option label="进行中" :value="1"></el-option>
        <el-option label="已完成" :value="0"></el-option>
      </el-select>
      <el-button type="primary" @click="handleSearch">搜索</el-button>
      <el-button @click="handleReset">重置</el-button>
    </div>

    <!-- 统计信息 -->
    <div class="forum-stats">
      <span>共 <strong>{{ total }}</strong> 条事项</span>
    </div>

    <!-- 帖子卡片列表 -->
    <div v-loading="loading" class="post-list">
      <div
        v-for="item in tableData"
        :key="item.id"
        class="post-card"
        @click="openPostDetail(item)"
      >
        <div class="post-card-header">
          <el-tag
            :type="item.status == 0 ? 'success' : 'warning'"
            size="mini"
            class="post-status-tag"
          >
            {{ item.status == 0 ? '已完成' : '进行中' }}
          </el-tag>
          <h3 class="post-title">{{ item.title }}</h3>
        </div>
        <div class="post-meta">
          <el-tag type="info" size="mini" effect="plain">
            <i class="el-icon-folder-opened"></i>
            {{ item.department }}
          </el-tag>
          <span v-if="item.deadline" class="post-deadline">
            <i class="el-icon-time"></i>
            {{ formatDate(item.deadline) }}
          </span>
        </div>
        <p v-if="item.content" class="post-preview">{{ truncateText(item.content, 120) }}</p>
        <div class="post-card-footer">
        <span class="post-comment-count">
          <i class="el-icon-chat-dot-round"></i>
          {{ item.commentCount || 0 }} 条评论
        </span>
        <div class="post-actions" @click.stop>
          <el-button type="primary" link size="mini" @click="openPostDetail(item)">
            <i class="el-icon-view"></i> 查看
          </el-button>
          <el-button v-if="operatePermission()" type="primary" link size="mini" @click="handleEdit(item)">
            <i class="el-icon-edit"></i> 编辑
          </el-button>
          <el-button v-if="operatePermission()" type="danger" link size="mini" @click="handleDelete(item)">
            <i class="el-icon-delete"></i> 删除
          </el-button>
        </div>
      </div>
      </div>

      <!-- 空状态 -->
      <el-empty v-if="!loading && tableData.length === 0" description="暂无事项"></el-empty>
    </div>

    <!-- 分页 -->
    <el-pagination
      :current-page="page"
      :page-sizes="[10, 20, 50, 100]"
      :page-size="pageSize"
      :total="total"
      layout="total, sizes, prev, pager, next, jumper"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
      class="forum-pagination"
    ></el-pagination>
    </div>
    <!-- /事项 Tab 容器 -->

    <!-- 图片 Tab 容器 -->
    <div v-if="activeTab === 'image'" class="forum-image-wrapper">
      <div v-loading="imageLoading" class="image-grid">
        <div
          v-for="(img, idx) in imageList"
          :key="img.id"
          class="image-card"
          :style="{ aspectRatio: img.ratio }"
          @click="previewImage(img)"
        >
          <!-- 加载中: 占位 -->
          <div v-if="!img._loaded" class="image-card-skeleton">
            <i class="el-icon-picture-outline"></i>
          </div>
          <img
            v-show="img._loaded"
            :src="img.url"
            :ref="el => bindImgRef(el, idx)"
            :alt="img.id"
            class="image-thumb"
            loading="lazy"
            decoding="async"
            @load="onImgLoad(idx)"
            @error="onImgError(idx)"
          />
          <!-- 加载失败: 友好提示 -->
          <div v-if="img._error" class="image-card-error">
            <i class="el-icon-warning-outline"></i>
            <span>加载失败</span>
            <el-button type="text" class="retry-btn" @click.stop="retryImage(idx)">重试</el-button>
          </div>
          <!-- 删除按钮: 加载完成(无论成功/失败)都显示, 方便删除失败图片 -->
          <div v-if="img._loaded" class="image-card-mask">
            <el-button
              type="danger"
              icon="el-icon-delete"
              size="mini"
              circle
              class="image-card-del"
              @click.stop="removeImage(idx)"
            ></el-button>
          </div>
        </div>
        <!-- 添加占位卡片 -->
        <div class="image-card image-card-add" @click="openImageDialog">
          <i class="el-icon-plus"></i>
          <span>添加图片</span>
        </div>
      </div>
      <el-empty v-if="!imageLoading && imageList.length === 0" description="暂无图片, 点击右上角添加"></el-empty>
    </div>

    <!-- 图片大图预览 -->
    <el-dialog
      :visible.sync="imagePreviewVisible"
      title="图片预览"
      width="720px"
      top="6vh"
      append-to-body
    >
      <div class="image-preview-wrap" :style="{ aspectRatio: imagePreviewing.ratio }">
        <img :src="imagePreviewing.url" alt="图片" class="image-preview-img" />
      </div>
    </el-dialog>

    <!-- 添加图片对话框 -->
    <el-dialog
      :visible.sync="imageDialogVisible"
      title="添加图片"
      width="560px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="!imageSubmitting"
      append-to-body
    >
      <div v-loading="imageSubmitting || imageReading" :element-loading-text="imageSubmitting ? '图片上传中，请稍候...' : '图片处理中，请稍候...'">
      <el-form ref="imageFormRef" :model="imageForm" label-width="100px">
        <el-form-item label="上传图片">
          <el-upload
            ref="imageUploadRef"
            class="image-uploader"
            action="#"
            :auto-upload="false"
            :show-file-list="false"
            :on-change="handleImageChange"
            :before-upload="beforeImageUpload"
            accept="image/*"
          >
            <div v-if="imageForm.preview" class="upload-preview">
              <img :src="imageForm.preview" alt="预览" class="upload-preview-img" />
              <div class="upload-preview-mask">点击替换</div>
            </div>
            <div v-else class="upload-trigger">
              <i class="el-icon-upload"></i>
              <span>点击选择图片</span>
            </div>
          </el-upload>
          <div class="upload-tip">支持 jpg/png/gif/webp 等常见图片格式, 大小不超过 5M</div>
        </el-form-item>
        <el-form-item label="长宽比例">
          <div class="ratio-row">
            <el-radio-group v-model="imageForm.ratioMode">
              <el-radio label="16:9">16:9</el-radio>
              <el-radio label="4:3">4:3</el-radio>
            </el-radio-group>
          </div>
          <div v-if="imageForm.preview" class="ratio-preview" :style="ratioPreviewStyle">
            <img :src="imageForm.preview" alt="预览" class="ratio-preview-img" />
          </div>
        </el-form-item>
      </el-form>
      </div>
      <div slot="footer">
        <el-button @click="imageDialogVisible = false" :disabled="imageSubmitting">取 消</el-button>
        <el-button type="primary" :loading="imageSubmitting" @click="submitImage">保 存</el-button>
      </div>
    </el-dialog>

    <!-- 帖子详情对话框 -->
    <el-dialog
      :visible.sync="detailVisible"
      :title="currentPost ? currentPost.title : '事项详情'"
      width="1000px"
      top="5vh"
      class="post-detail-dialog"
      @close="handleDetailClose"
    >
      <div v-if="currentPost">
        <div class="detail-meta">
          <el-tag :type="currentPost.status == 0 ? 'success' : 'warning'" size="mini">
            {{ currentPost.status == 0 ? '已完成' : '进行中' }}
          </el-tag>
          <el-tag type="info" size="mini" effect="plain" class="ml-2">
            {{ currentPost.department }}
          </el-tag>
          <span v-if="currentPost.deadline" class="detail-deadline ml-2">
            <i class="el-icon-time"></i> 截止日期: {{ formatDate(currentPost.deadline) }}
          </span>
        </div>

        <div class="detail-content" v-html="currentPost.content ? currentPost.content.replace(/\n/g, '<br>') : '暂无内容'">
        </div>

        <!-- 评论区 -->
        <el-divider></el-divider>
        <div class="comment-section">
          <h4 class="comment-title">
            <i class="el-icon-chat-dot-round"></i>
            评论 ({{ commentTotal }})
          </h4>

          <!-- 评论列表 -->
          <div v-loading="commentLoading" class="comment-list">
            <div
              v-for="comment in topLevelComments"
              :key="comment.id"
              class="comment-item"
            >
              <div class="comment-avatar">
                {{ getAvatarText(comment.trueName || comment.userName) }}
              </div>
              <div class="comment-body">
                <div class="comment-header">
                  <span class="comment-username">
                    {{ comment.trueName || comment.userName || '匿名用户' }}
                  </span>
                  <span class="comment-time">{{ formatTime(comment.createdAt) }}</span>
                </div>
                <p class="comment-content">{{ comment.content }}</p>
                <div class="comment-actions">
                  <span
                    class="like-btn"
                    :class="{ liked: comment.isLiked }"
                    @click="handleLike(comment)"
                  >
                    <img :src="comment.isLiked ? require('@/assets/img/liked.png') : require('@/assets/img/like.png')" class="action-icon-thumb" alt="赞" />
                    <span v-if="(comment.likeCount || 0) > 0" class="like-count">{{ comment.likeCount }}</span>
                  </span>
                  <span
                    class="dislike-btn"
                    :class="{ disliked: comment.isDisliked }"
                    @click="handleDislike(comment)"
                  >
                    <img :src="comment.isDisliked ? require('@/assets/img/liked.png') : require('@/assets/img/like.png')" class="action-icon-thumb flip" alt="踩" />
                    <span v-if="(comment.dislikeCount || 0) > 0" class="dislike-count">{{ comment.dislikeCount }}</span>
                  </span>
                  <el-button type="primary" link size="mini" @click="startReply(comment)">
                    回复
                  </el-button>
                  <el-button
                    v-if="canDeleteComment(comment)"
                    type="danger"
                    link
                    size="mini"
                    @click="handleDeleteComment(comment)"
                  >
                    删除
                  </el-button>
                </div>

                <!-- 回复输入框 -->
                <div v-if="replyTo === comment.id" class="reply-input-box">
                  <div class="mention-textarea-wrapper">
                    <mention-popup
                      :visible="replyMention.state.mentionVisible"
                      :users="replyMention.state.filteredUsers"
                      :active-index="replyMention.state.mentionActiveIdx"
                      :keyword="replyMention.state.mentionKeyword"
                      :loading="replyMention.state.loading"
                      @select="onReplyMentionSelect"
                      @update:activeIndex="replyMention.state.mentionActiveIdx = $event"
                      @close="replyMention.closeMention()"
                    />
                    <department-popup
                      :visible="replyDept.state.deptVisible"
                      :departments="replyDept.state.filteredDepartments"
                      :active-index="replyDept.state.deptActiveIdx"
                      :keyword="replyDept.state.deptKeyword"
                      @select="onReplyDeptSelect"
                      @update:activeIndex="replyDept.state.deptActiveIdx = $event"
                      @close="replyDept.closeDepartment()"
                    />
                    <el-input
                      ref="replyTextareaRef"
                      v-model="replyText"
                      type="textarea"
                      :rows="2"
                      :placeholder="'回复 ' + (comment.trueName || comment.userName || '用户') + '：（输入@可提及用户，#可提及部门）'"
                      maxlength="500"
                      show-word-limit
                      @input="onReplyInput"
                      @keydown.native="onReplyKeydown"
                      @blur="onReplyBlur"
                    />
                  </div>
                  <div class="reply-input-btns">
                    <el-button size="mini" @click="cancelReply">取消</el-button>
                    <el-button type="primary" size="mini" :loading="replySubmitting" @click="submitReply(comment)">
                      回复
                    </el-button>
                  </div>
                </div>

                <!-- 子评论 -->
                <div v-if="comment.children && comment.children.length > 0" class="nested-replies">
                  <reply-item
                    v-for="reply in comment.children"
                    :key="reply.id"
                    :reply="reply"
                    @delete="handleDeleteComment"
                    @refreshed="fetchComments"
                  />
                </div>
              </div>
            </div>

            <el-empty v-if="!commentLoading && commentList.length === 0" description="暂无评论，快来发表第一条评论吧"></el-empty>
          </div>

          <!-- 发表评论 -->
          <div class="comment-input-area">
            <div class="mention-textarea-wrapper">
              <mention-popup
                :visible="mainMention.state.mentionVisible"
                :users="mainMention.state.filteredUsers"
                :active-index="mainMention.state.mentionActiveIdx"
                :keyword="mainMention.state.mentionKeyword"
                :loading="mainMention.state.loading"
                @select="onMainMentionSelect"
                @update:activeIndex="mainMention.state.mentionActiveIdx = $event"
                @close="mainMention.closeMention()"
              />
              <department-popup
                :visible="commentDept.state.deptVisible"
                :departments="commentDept.state.filteredDepartments"
                :active-index="commentDept.state.deptActiveIdx"
                :keyword="commentDept.state.deptKeyword"
                @select="onCommentDeptSelect"
                @update:activeIndex="commentDept.state.deptActiveIdx = $event"
                @close="commentDept.closeDepartment()"
              />
              <el-input
                ref="commentTextareaRef"
                v-model="commentText"
                type="textarea"
                :rows="3"
                placeholder="写下你的评论...（输入@可以提及用户，#可提及部门）"
                maxlength="500"
                show-word-limit
                @input="onCommentInput"
                @keydown.native="onCommentKeydown"
                @blur="onCommentBlur"
              />
            </div>
            <el-button
              type="primary"
              class="comment-submit"
              :loading="commentSubmitting"
              @click="handleSubmitComment"
            >
              发表评论
            </el-button>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :title="dialogTitle"
      width="600px"
      @close="handleDialogClose"
    >
      <el-form :model="formData" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="formData.title" placeholder="请输入事项标题"></el-input>
        </el-form-item>
        <el-form-item label="部门" prop="department">
          <el-select v-model="formData.department" placeholder="请选择部门" style="width: 100%" filterable>
            <el-option
              v-for="dept in departments"
              :key="dept.id"
              :label="dept.name"
              :value="dept.name"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="内容" prop="content">
          <div class="mention-textarea-wrapper">
            <mention-popup
              :visible="contentMention.state.mentionVisible"
              :users="contentMention.state.filteredUsers"
              :active-index="contentMention.state.mentionActiveIdx"
              :keyword="contentMention.state.mentionKeyword"
              :loading="contentMention.state.loading"
              @select="onContentMentionSelect"
              @update:activeIndex="contentMention.state.mentionActiveIdx = $event"
              @close="contentMention.closeMention()"
            />
            <department-popup
              :visible="contentDept.state.deptVisible"
              :departments="contentDept.state.filteredDepartments"
              :active-index="contentDept.state.deptActiveIdx"
              :keyword="contentDept.state.deptKeyword"
              @select="onContentDeptSelect"
              @update:activeIndex="contentDept.state.deptActiveIdx = $event"
              @close="contentDept.closeDepartment()"
            />
            <el-input
              ref="contentTextareaRef"
              v-model="formData.content"
              type="textarea"
              :rows="6"
              placeholder="请输入事项详情...（输入@可提及用户，#可提及部门）"
              maxlength="500"
              show-word-limit
              @input="onContentInput"
              @keydown.native="onContentKeydown"
              @blur="onContentBlur"
            ></el-input>
          </div>
        </el-form-item>
        <el-form-item label="截止时间" prop="deadline">
          <el-date-picker
            v-model="formData.deadline"
            type="date"
            placeholder="请选择截止日期"
            style="width: 100%"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="formData.status">
            <el-radio :label="1">进行中</el-radio>
            <el-radio :label="0">已完成</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  getAffairs,
  createAffair,
  updateAffair,
  deleteAffair,
  getComments,
  createComment,
  deleteComment,
  toggleLike,
  toggleDislike,
  getMessages,
  createMessage,
  deleteMessage,
  getSimpleUsers,
  queryAllDepartment,
  uploadAffairImage,
  deleteAffairImage
} from "@/api/screen";
import { getAllDepartments } from "@/api/common";
import { useMention } from "@/composables/useMention";
import { useDepartmentMention } from "@/composables/useDepartmentMention";
import MentionPopup from "./components/MentionPopup.vue";
import DepartmentPopup from "./components/DepartmentPopup.vue";
import ReplyItem from "./components/ReplyItem.vue";
import { filterSensitiveWords } from '@/utils/sensitiveWords'

export default {
  name: "ScreenTodoList",
  components: {
    MentionPopup,
    DepartmentPopup,
    ReplyItem
  },
  data() {
    return {
      loading: false,
      tableData: [],
      departments: [],
      searchForm: {
        keyword: '',
        department: '',
        status: ''
      },
      page: 1,
      pageSize: 10,
      total: 0,
      dialogVisible: false,
      dialogTitle: '添加事项',
      formRef: null,
      formData: {
        id: null,
        title: '',
        department: '',
        content: '',
        status: 1,
        deadline: '',
        // 事项类型：1=文字待办，2=图片。新增时必须显式传值，
        // 否则后端 insert 会写入 NULL，导致列表按 type 过滤时该记录不可见
        type: 1
      },
      rules: {
        title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
        department: [{ required: true, message: '请选择部门', trigger: 'change' }],
        deadline: [{ required: true, message: '请选择截止时间', trigger: 'change' }]
      },
      detailVisible: false,
      currentPost: null,
      commentList: [],
      commentTotal: 0,
      commentText: '',
      commentLoading: false,
      commentSubmitting: false,
      replyTo: null,
      replyText: '',
      replySubmitting: false,
      currentUser: null,
      mainMention: null,
      replyMention: null,
      contentMention: null,
      contentDept: null,
      commentDept: null,
      replyDept: null,
      commentTextareaRef: null,
      replyTextareaRef: null,
      contentTextareaRef: null,
      mainCursorAtDetect: 0,
      replyCursorAtDetect: 0,
      contentCursorAtDetect: 0,
      // ===== 图片 tab 相关 =====
      activeTab: 'todo', // 当前 tab: todo / image
      imageList: [],     // 已上传图片列表
      imageLoading: false,
      imageDialogVisible: false, // 添加图片弹窗
      imageSubmitting: false, // 上传中
      imageReading: false, // 本地图片读取(转 base64)中
      imageForm: {
        preview: '',
        ratioMode: '16:9',
        rawFile: null  // 保留原始文件对象用于上传
      },
      imagePreviewVisible: false,
      imagePreviewing: { url: '', ratio: '16 / 9' }
    };
  },
  computed: {
    topLevelComments() {
      return this.commentList.filter(c => !c.parent_id && !c.parentId);
    },
    // 图片比例预览样式
    ratioPreviewStyle() {
      const m = this.imageForm.ratioMode
      return { aspectRatio: m.replace(':', ' / ') }
    },
    // 当前 form 实际 ratio (字符串)
    currentRatio() {
      return this.imageForm.ratioMode.replace(':', ' / ')
    }
  },
  created() {
    this.initUser();
    this.mainMention = useMention();
    this.replyMention = useMention();
    this.contentMention = useMention();
    this.contentDept = useDepartmentMention(queryAllDepartment);
    this.commentDept = useDepartmentMention(queryAllDepartment);
    this.replyDept = useDepartmentMention(queryAllDepartment);
    this.fetchData();
    this.fetchDepartments();
    // 进入页面预拉取图片 tab 的数据, 确保 tab 角标与列表正确显示
    this.loadAffairImages();
  },
  provide() {
    return {
      createComment: (affairId, data) => createComment(affairId, data),
      contentId: () => this.currentPost ? this.currentPost.id : null,
      userStore: {
        userInfo: this.currentUser
      }
    };
  },
  methods: {
    // ====== 图片 tab 相关方法 ======
    /** 打开添加图片弹窗 */
    /** 切换 tab + 按需拉取 */
    switchTab(tab) {
      this.activeTab = tab
      if (tab === 'image' && this.imageList.length === 0) {
        this.loadAffairImages()
      }
    },
    /** 拉取事项配图列表 (type=2) */
    async loadAffairImages() {
      this.imageLoading = true
      try {
        const resp = await getAffairs({ page: 1, limit: 100, type: 2 })
        if (resp && resp.code === 200) {
          const rows = resp.data || []
          this.imageList = (rows || []).map(r => {
            // 缩略图优先, 没有则回落到原图
            const listUrl = r.thumbUrl || r.imgUrl || r.url || r.fileUrl || r.path
            // 大图预览仍走原图
            const fullUrl = r.imgUrl || r.url || r.fileUrl || r.path
            return {
              id: r.id,
              // 列表用缩略图, 体积更小加载更快
              url: this.resolveImageUrl(listUrl),
              fullUrl: this.resolveImageUrl(fullUrl), // 预览大图用
              ratio: (r.imgRatio || '16:9').replace(':', ' / '),
              _loaded: false,  // 标记是否加载完成
              _error: false     // 标记是否加载失败
            }
          })
        } else {
          this.$message.error((resp && resp.msg) || '加载图片列表失败')
        }
      } catch (e) {
        console.error('加载图片列表失败：', e)
        this.$message.error('加载图片列表失败')
      } finally {
        this.imageLoading = false
      }
    },
    /**
     * 解析图片完整 URL
     * 1) 如果已是 http/https 数据 URL, 直接返回
     * 2) 拼接 .env.development 中的 VUE_APP_PREVIEW_FILE_URL
     * 3) 将 /home/file_upload 替换为 /files, 避开老路径
     * 例: imgUrl=/home/file_upload/affair/xxx.jpg
     *     => http://172.16.0.113:8088/files/affair/xxx.jpg
     */
    resolveImageUrl(path) {
      if (!path) return ''
      // 已是完整 URL(包含协议)或 base64 data URL, 原样返回
      if (/^(https?:|data:)/i.test(path)) return path
      const base = process.env.VUE_APP_PREVIEW_FILE_URL || ''
      // 把 /home/file_upload 替换为 /files
      const normalized = String(path).replace('/home/file_upload', '/files')
      const baseNoSlash = base.replace(/\/+$/, '')
      const pathWithSlash = normalized.startsWith('/') ? normalized : '/' + normalized
      return baseNoSlash + pathWithSlash
    },
    openImageDialog() {
      this.imageForm = {
        preview: '',
        ratioMode: '16:9',
        rawFile: null
      }
      this.imageReading = false
      this.imageSubmitting = false
      this.imageDialogVisible = true
    },
    /** 上传前校验: 限制图片大小 5M */
    beforeImageUpload(file) {
      const isImage = /^image\//.test(file.type)
      if (!isImage) {
        this.$message.warning('只能上传图片文件')
        return false
      }
      const isLt5M = file.size / 1024 / 1024 <= 5
      if (!isLt5M) {
        this.$message.warning('图片大小不能超过 5M')
        return false
      }
      return true
    },
    /** 处理 el-upload 文件变化 */
    handleImageChange(file) {
      const raw = file && file.raw
      if (!raw) return
      if (!/^image\//.test(raw.type)) {
        this.$message.warning('请上传图片文件')
        return
      }
      this.imageForm.rawFile = raw  // 保留原始文件对象,提交时上传
      // 大图转 base64 预览较耗时, 加 loading
      this.imageReading = true
      const reader = new FileReader()
      reader.onload = e => {
        this.imageForm.preview = e.target.result
        this.imageReading = false
      }
      reader.onerror = () => {
        this.imageReading = false
        this.$message.error('图片读取失败, 请重新选择')
      }
      reader.readAsDataURL(raw)
    },
    /** 切换比例时, 自定义模式不强制 */
    onRatioModeChange() {
      // 留给以后做限制(例如自定义范围)
    },
    /** 保存图片(本地 mock, 不接接口) */
    submitImage() {
      if (!this.imageForm.rawFile) {
        this.$message.warning('请先选择图片')
        return
      }
      // type: 2 = 事项配图 (与后端 /affair/image/upload 约定)
      // imgRatio: '16:9' / '4:3'
      this.imageSubmitting = true
      uploadAffairImage(this.imageForm.rawFile, this.imageForm.ratioMode, 2)
        .then(res => {
          // 统一后端响应兼容: res.data || res || {}
          const payload = (res && (res.data || res)) || {}
          // 后端返回的字段约定: id / imgUrl / url
          const resolvedUrl = payload.imgUrl || payload.url
          const item = {
            id: payload.id || Date.now(),
            // 优先用后端返回的 imgUrl/url 经 resolveImageUrl 处理; 兜底使用本地 base64 预览
            url: resolvedUrl ? this.resolveImageUrl(resolvedUrl) : this.imageForm.preview,
            ratio: this.currentRatio,
            createdAt: new Date(),
            // 若是 base64 兜底, 立即标记为已加载(无需等待)
            _loaded: !!(resolvedUrl && this.resolveImageUrl(resolvedUrl)),
            _error: false
          }
          this.imageList.unshift(item)
          this.imageSubmitting = false
          this.imageDialogVisible = false
          this.$message.success('图片上传成功')
        })
        .catch(err => {
          this.imageSubmitting = false
          console.error('上传图片失败：', err)
          this.$message.error(err && err.message ? '上传失败:' + err.message : '上传失败')
        })
    },
    /** 大图预览 */
    previewImage(img) {
      // 加载失败的图不允许预览
      if (img._error) return
      this.imagePreviewing = {
        // 预览大图走 fullUrl(原图), 没有时回落到 url
        url: img.fullUrl || img.url,
        ratio: img.ratio
      }
      this.imagePreviewVisible = true
    },
    /** 缩略图加载成功 */
    onImgLoad(idx) {
      if (this.imageList[idx]) {
        this.imageList[idx]._loaded = true
        this.imageList[idx]._error = false
      }
    },
    /** 缩略图加载失败 */
    onImgError(idx) {
      if (this.imageList[idx]) {
        this.imageList[idx]._loaded = true
        this.imageList[idx]._error = true
      }
    },
    /**
     * @imgRef 绑定函数(用于获取真实 DOM 节点)
     * 拿到 DOM 后: 1) 立即检查 naturalWidth; 2) 设置 3 秒超时兜底
     * 避免 loading="lazy" 配合 v-show 时, @load 不触发的卡骨架问题
     */
    bindImgRef(el, idx) {
      if (!el || !this.imageList[idx]) return
      const item = this.imageList[idx]
      // 已加载完成的不再处理
      if (item._loaded) return
      // 1) 立即检查 (图片可能已通过缓存加载完成)
      if (el.complete) {
        if (el.naturalWidth > 0) {
          item._loaded = true
          item._error = false
        } else {
          item._loaded = true
          item._error = true
        }
        return
      }
      // 2) 3 秒超时兜底: 任何状态下强制结束骨架态
      setTimeout(() => {
        if (item._loaded) return
        if (el.naturalWidth > 0) {
          item._loaded = true
          item._error = false
        } else if (el.complete && el.naturalWidth === 0) {
          item._loaded = true
          item._error = true
        } else {
          // 仍在加载中, 强制展示(避免永远卡骨架)
          item._loaded = true
          item._error = false
        }
      }, 3000)
    },
    /** 重试加载失败的图片 */
    retryImage(idx) {
      const item = this.imageList[idx]
      if (!item) return
      // 简单强制重试: 拼接时间戳避免缓存
      item._loaded = false
      item._error = false
      item.url = item.url.split('?')[0] + (item.url.includes('?') ? '&' : '?') + 't=' + Date.now()
    },
    /** 删除图片(联调) */
    removeImage(idx) {
      const item = this.imageList[idx]
      // 兜底: 本地上传但后端未返回 id 的, 走纯本地删除
      if (!item || !item.id || typeof item.id !== 'number') {
        this.imageList.splice(idx, 1)
        this.$message.success('已删除')
        return
      }
      this.$confirm('确认删除该图片?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return deleteAffairImage(item.id)
      }).then(resp => {
        if (resp && resp.code === 200) {
          this.imageList.splice(idx, 1)
          this.$message.success('已删除')
        } else {
          this.$message.error((resp && resp.msg) || '删除失败')
        }
      }).catch(err => {
        // 用户取消不会进这里; 真正的接口错误才进
        if (err && err !== 'cancel') {
          console.error('删除图片失败：', err)
          this.$message.error('删除失败')
        }
      })
    },
    /**
     * 判断当前用户是否有事项操作权限（发布、编辑、删除）
     * @returns {boolean} - 有权限返回true，无权限返回false
     * 权限规则：
     * - 角色ID在允许列表中：1(系统管理员), 8(总经理), 17(销售总监), 26(集成部经理), 29(人力资源部总监), 
     *   32(数据智研部总监), 34(数据智研部经理), 35(数据治理部经理), 36(数据库服务部总监), 
     *   38(数据库服务部经理), 42(数据安全服务部总监), 43(数据安全服务部经理), 44(售前服务部经理), 
     *   46(集成部技术总监), 48(行政总监), 49(售前服务部总监), 50(工程部总监), 54(CTO)
     * - 用户ID在允许列表中：92(罗瑞文), 96(陈斌), 101(周林英), 99(李星), 141(刘冀军)
     * - 满足任一条件即可获得操作权限
     */
    operatePermission() {
      // 从sessionStorage获取当前用户的角色ID
      const userRoleId = sessionStorage.getItem("userRoleId");
      // 从sessionStorage获取当前用户的用户ID
      const uid = sessionStorage.getItem("uid");
      
      // 有权限操作事项的角色ID清单
      const allowedRoleIds = ["1", "8", "17", "26", "29", "32", "34", "35", "36", "38", "42", "43", "44", "46", "48", "49", "50", "54"];
      
      // 有权限操作事项的用户ID清单
      const allowedUserIds = ["92", "96", "101", "99", "141", "215"];
      
      // 判断是否满足权限条件：角色ID匹配 或 用户ID匹配，任一满足即可
      return allowedRoleIds.includes(userRoleId) || allowedUserIds.includes(uid);
    },

    initUser() {
      const userStr = window.sessionStorage.getItem("user");
      if (userStr) {
        try {
          this.currentUser = JSON.parse(userStr);
        } catch (e) {
          this.currentUser = null;
        }
      }
    },
    getAllDescendants(parentId) {
      const result = [];
      const stack = [parentId];
      while (stack.length) {
        const pid = stack.pop();
        const kids = this.commentList.filter(c => (c.parent_id === pid || c.parentId === pid));
        kids.forEach(c => { result.push(c); stack.push(c.id); });
      }
      return result.sort((a, b) => new Date(a.created_at || a.createTime) - new Date(b.created_at || b.createTime));
    },
    truncateText(text, maxLen) {
      if (!text) return '';
      return text.length > maxLen ? text.slice(0, maxLen) + '...' : text;
    },
    getAvatarText(name) {
      if (!name) return '?';
      return name.charAt(0).toUpperCase();
    },
    formatTime(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const now = new Date();
      const diff = now - d;
      if (diff < 60000) return '刚刚';
      if (diff < 3600000) return Math.floor(diff / 60000) + '分钟前';
      if (diff < 86400000) return Math.floor(diff / 3600000) + '小时前';
      if (diff < 604800000) return Math.floor(diff / 86400000) + '天前';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${mins}`;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      if (typeof dateStr === 'string' && dateStr.length === 10) return dateStr;
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    canDeleteComment(comment) {
      if (!this.currentUser) return false;
      const roleId = window.sessionStorage.getItem("userRoleId");
      return roleId === '1' || this.currentUser.userName === comment.userName;
    },
    fetchData() {
      this.loading = true;
      getAffairs({
        page: this.page,
        limit: this.pageSize,
        type: 1,
        ...this.searchForm
      }).then(resp => {
        if (resp && resp.code === 200) {
          this.tableData = resp.data || [];
          this.total = resp.total || 0;
        }
      }).catch(() => {
        this.$message.error('获取数据失败');
      }).finally(() => {
        this.loading = false;
      });
    },
    handlePageChange(val) {
      this.page = val;
      this.fetchData();
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.page = 1;
      this.fetchData();
    },
    handleSearch() {
      this.page = 1;
      this.fetchData();
    },
    handleReset() {
      this.searchForm = { keyword: '', department: '', status: '' };
      this.handleSearch();
    },
    fetchDepartments() {
      getAllDepartments().then(resp => {
        if (resp && resp.result) {
          this.departments = resp.result || [];
        }
      }).catch(() => {});
    },
    handleAdd() {
      this.dialogTitle = '添加事项';
      this.dialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.formRef) {
          this.$refs.formRef.resetFields();
        }
      });
      this.formData = {
        id: null,
        title: '',
        department: '',
        content: '',
        status: 1,
        deadline: '',
        type: 1
      };
    },
    handleEdit(row) {
      this.dialogTitle = '编辑事项';
      const editData = { ...row };
      if (editData.deadline) {
        if (typeof editData.deadline === 'string' && editData.deadline.includes('T')) {
          editData.deadline = editData.deadline.split('T')[0];
        }
      }
      this.formData = editData;
      this.dialogVisible = true;
    },
    handleDelete(row) {
      this.$confirm('确定要删除该事项吗？相关评论也会被删除。', '提示', {
        type: 'warning'
      }).then(() => {
        deleteAffair(row.id).then(resp => {
          if (resp && resp.code === 200) {
            this.$message.success('删除成功');
            this.fetchData();
          }
        }).catch(() => {
          this.$message.error('删除失败');
        });
      }).catch(() => {});
    },
    handleSubmit() {
      this.$refs.formRef.validate(valid => {
        if (!valid) return;
        const submitData = { ...this.formData };
        const action = this.formData.id ? updateAffair(submitData) : createAffair(submitData);
        action.then(resp => {
          if (resp && resp.code === 200) {
            this.$message.success(this.formData.id ? '更新成功' : '发布成功');
            this.dialogVisible = false;
            this.fetchData();
          }
        }).catch(() => {
          this.$message.error('操作失败');
        });
      });
    },
    handleDialogClose() {
      if (this.$refs.formRef) {
        this.$refs.formRef.resetFields();
      }
      this.formData = {
        id: null,
        title: '',
        department: '',
        content: '',
        status: 1,
        deadline: '',
        type: 1
      };
    },
    openPostDetail(item) {
      this.currentPost = item;
      this.detailVisible = true;
      this.fetchComments();
    },
    handleDetailClose() {
      this.currentPost = null;
      this.commentList = [];
      this.commentTotal = 0;
      this.commentText = '';
      this.replyTo = null;
      this.replyText = '';
    },
    fetchComments() {
      if (!this.currentPost) return;
      this.commentLoading = true;
      getComments(this.currentPost.id, { limit: 200 }).then(resp => {
        if (resp && resp.code === 200) {
          this.commentList = resp.data || [];
          this.commentTotal = resp.total || 0;
        } else {
          this.$message.error(resp.msg || '获取评论失败');
        }
      }).catch(() => {
        this.$message.error('获取评论失败');
      }).finally(() => {
        this.commentLoading = false;
      });
    },
    handleSubmitComment() {
      if (!this.commentText.trim()) {
        this.$message.warning('请输入评论内容');
        return;
      }
      const { text, matchCount } = filterSensitiveWords(this.commentText.trim())
      if (matchCount > 0) {
        this.$message.warning(`内容包含 ${matchCount} 个敏感词，已自动替换为*`)
        this.commentText = text
      }
      this.commentSubmitting = true;
      createComment(this.currentPost.id, {
        content: text
      }).then(resp => {
        if (resp && resp.code === 200) {
          this.$message.success('评论成功');
          this.commentText = '';
          this.fetchComments();
          if (this.currentPost) {
            this.currentPost.commentCount = (this.currentPost.commentCount || 0) + 1;
          }
        }
      }).catch(() => {
        this.$message.error('评论失败');
      }).finally(() => {
        this.commentSubmitting = false;
      });
    },
    startReply(comment) {
      this.replyTo = comment.id
      this.replyText = ''
    },
    submitReply(parentComment) {
      if (!this.replyText.trim()) {
        this.$message.warning('请输入回复内容')
        return
      }
      const { text, matchCount } = filterSensitiveWords(this.replyText.trim())
      if (matchCount > 0) {
        this.$message.warning(`内容包含 ${matchCount} 个敏感词，已自动替换为*`)
        this.replyText = text
      }
      this.replySubmitting = true
      createComment(this.currentPost.id, {
        content: text,
        parentId: parentComment.id
      }).then(resp => {
        if (resp && resp.code === 200) {
          this.$message.success('回复成功')
          this.replyText = ''
          this.replyTo = null
          this.replyMention.closeMention()
          this.fetchComments()
          if (this.currentPost) {
            this.currentPost.commentCount = (this.currentPost.commentCount || 0) + 1
          }
        }
      }).catch(() => {
        this.$message.error('回复失败')
      }).finally(() => {
        this.replySubmitting = false
      })
    },
    cancelReply() {
      this.replyTo = null
      this.replyText = ''
      this.replyMention.closeMention()
    },
    getTextareaCursor(elRef) {
      if (!elRef || !elRef.$el) return 0
      const el = elRef.$el || elRef
      const textarea = el.querySelector ? el.querySelector('textarea') : el
      return textarea ? textarea.selectionEnd : 0
    },
    setTextareaCursor(elRef, pos) {
      this.$nextTick(() => {
        const el = elRef && (elRef.$el || elRef)
        const textarea = el && (el.querySelector ? el.querySelector('textarea') : el)
        if (textarea) {
          textarea.selectionStart = textarea.selectionEnd = pos
          textarea.focus()
        }
      })
    },
    onCommentInput(val) {
      this.$nextTick(() => {
        const cursor = this.getTextareaCursor(this.$refs.commentTextareaRef)
        this.mainCursorAtDetect = cursor
        this.mainMention.detectMention(val, cursor)
        this.commentDept.detectDepartment(val, cursor)
      })
    },
    onReplyInput(val) {
      this.$nextTick(() => {
        const refEl = Array.isArray(this.$refs.replyTextareaRef)
          ? this.$refs.replyTextareaRef[0]
          : this.$refs.replyTextareaRef
        const cursor = this.getTextareaCursor(refEl)
        this.replyCursorAtDetect = cursor
        this.replyMention.detectMention(val, cursor)
        this.replyDept.detectDepartment(val, cursor)
      })
    },
    onCommentKeydown(e) {
      if (this.commentDept.state.deptVisible) {
        if (e.key === 'ArrowDown') { e.preventDefault(); this.commentDept.moveDown() }
        else if (e.key === 'ArrowUp') { e.preventDefault(); this.commentDept.moveUp() }
        else if (e.key === 'Enter') { e.preventDefault(); this.applyCommentDept() }
        else if (e.key === 'Escape') { e.preventDefault(); this.commentDept.closeDepartment() }
        return
      }
      if (!this.mainMention.state.mentionVisible) return
      if (e.key === 'ArrowDown') { e.preventDefault(); this.mainMention.moveDown() }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.mainMention.moveUp() }
      else if (e.key === 'Enter') { e.preventDefault(); this.applyMainMention() }
      else if (e.key === 'Escape') { e.preventDefault(); this.mainMention.closeMention() }
    },
    onReplyKeydown(e) {
      if (this.replyDept.state.deptVisible) {
        if (e.key === 'ArrowDown') { e.preventDefault(); this.replyDept.moveDown() }
        else if (e.key === 'ArrowUp') { e.preventDefault(); this.replyDept.moveUp() }
        else if (e.key === 'Enter') { e.preventDefault(); this.applyReplyDept() }
        else if (e.key === 'Escape') { e.preventDefault(); this.replyDept.closeDepartment() }
        return
      }
      if (!this.replyMention.state.mentionVisible) return
      if (e.key === 'ArrowDown') { e.preventDefault(); this.replyMention.moveDown() }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.replyMention.moveUp() }
      else if (e.key === 'Enter') { e.preventDefault(); this.applyReplyMention() }
      else if (e.key === 'Escape') { e.preventDefault(); this.replyMention.closeMention() }
    },
    onCommentBlur() {
      this.mainMention.scheduleClose()
      this.commentDept.scheduleClose()
    },
    onReplyBlur() {
      this.replyMention.scheduleClose()
      this.replyDept.scheduleClose()
    },
    applyMainMention(user) {
      const result = user ? this.mainMention.selectUser(user) : this.mainMention.confirmSelection()
      if (!result) return
      const before = this.commentText.slice(0, result.anchorPos)
      const after = this.commentText.slice(this.mainCursorAtDetect)
      this.commentText = before + result.displayName + after
      this.setTextareaCursor(this.$refs.commentTextareaRef, result.cursorAfter)
    },
    applyReplyMention(user) {
      const result = user ? this.replyMention.selectUser(user) : this.replyMention.confirmSelection()
      if (!result) return
      const before = this.replyText.slice(0, result.anchorPos)
      const after = this.replyText.slice(this.replyCursorAtDetect)
      this.replyText = before + result.displayName + after
      const refEl = Array.isArray(this.$refs.replyTextareaRef)
        ? this.$refs.replyTextareaRef[0]
        : this.$refs.replyTextareaRef
      this.setTextareaCursor(refEl, result.cursorAfter)
    },
    onMainMentionSelect(user) { this.applyMainMention(user) },
    onReplyMentionSelect(user) { this.applyReplyMention(user) },
    applyCommentDept(dept) {
      const result = dept ? this.commentDept.selectDepartment(dept) : this.commentDept.confirmSelection()
      if (!result) return
      const before = this.commentText.slice(0, result.anchorPos)
      const after = this.commentText.slice(this.mainCursorAtDetect)
      this.commentText = before + result.displayName + after
      this.setTextareaCursor(this.$refs.commentTextareaRef, result.cursorAfter)
    },
    applyReplyDept(dept) {
      const result = dept ? this.replyDept.selectDepartment(dept) : this.replyDept.confirmSelection()
      if (!result) return
      const before = this.replyText.slice(0, result.anchorPos)
      const after = this.replyText.slice(this.replyCursorAtDetect)
      this.replyText = before + result.displayName + after
      const refEl = Array.isArray(this.$refs.replyTextareaRef)
        ? this.$refs.replyTextareaRef[0]
        : this.$refs.replyTextareaRef
      this.setTextareaCursor(refEl, result.cursorAfter)
    },
    onCommentDeptSelect(dept) { this.applyCommentDept(dept) },
    onReplyDeptSelect(dept) { this.applyReplyDept(dept) },
    onContentInput(val) {
      this.$nextTick(() => {
        const cursor = this.getTextareaCursor(this.$refs.contentTextareaRef)
        this.contentCursorAtDetect = cursor
        this.contentMention.detectMention(val, cursor)
        this.contentDept.detectDepartment(val, cursor)
      })
    },
    onContentKeydown(e) {
      // 优先处理部门 # 弹窗的快捷键
      if (this.contentDept.state.deptVisible) {
        if (e.key === 'ArrowDown') { e.preventDefault(); this.contentDept.moveDown() }
        else if (e.key === 'ArrowUp') { e.preventDefault(); this.contentDept.moveUp() }
        else if (e.key === 'Enter') { e.preventDefault(); this.applyContentDept() }
        else if (e.key === 'Escape') { e.preventDefault(); this.contentDept.closeDepartment() }
        return
      }
      // 处理 @ 用户弹窗的快捷键
      if (this.contentMention.state.mentionVisible) {
        if (e.key === 'ArrowDown') { e.preventDefault(); this.contentMention.moveDown() }
        else if (e.key === 'ArrowUp') { e.preventDefault(); this.contentMention.moveUp() }
        else if (e.key === 'Enter') { e.preventDefault(); this.applyContentMention() }
        else if (e.key === 'Escape') { e.preventDefault(); this.contentMention.closeMention() }
      }
    },
    onContentBlur() {
      this.contentMention.scheduleClose()
      this.contentDept.scheduleClose()
    },
    applyContentMention(user) {
      const result = user ? this.contentMention.selectUser(user) : this.contentMention.confirmSelection()
      if (!result) return
      const before = this.formData.content.slice(0, result.anchorPos)
      const after = this.formData.content.slice(this.contentCursorAtDetect)
      this.formData.content = before + result.displayName + after
      this.setTextareaCursor(this.$refs.contentTextareaRef, result.cursorAfter)
    },
    onContentMentionSelect(user) { this.applyContentMention(user) },
    applyContentDept(dept) {
      const result = dept ? this.contentDept.selectDepartment(dept) : this.contentDept.confirmSelection()
      if (!result) return
      const before = this.formData.content.slice(0, result.anchorPos)
      const after = this.formData.content.slice(this.contentCursorAtDetect)
      this.formData.content = before + result.displayName + after
      this.setTextareaCursor(this.$refs.contentTextareaRef, result.cursorAfter)
    },
    onContentDeptSelect(dept) { this.applyContentDept(dept) },
    handleLike(comment) {
      // if (!this.currentUser) {
      //   this.$message.warning('请先登录');
      //   return;
      // }
      toggleLike(comment.id).then(resp => {
        if (resp && resp.code === 200) {
          this.fetchComments();
        }
      });
    },
    handleDislike(comment) {
      // if (!this.currentUser) {
      //   this.$message.warning('请先登录');
      //   return;
      // }
      toggleDislike(comment.id).then(resp => {
        if (resp && resp.code === 200) {
          this.fetchComments();
        }
      });
    },
    handleDeleteComment(comment) {
      const descendantCount = this.getAllDescendants(comment.id).length;
      const warnMsg = descendantCount > 0
        ? '确定要删除这条评论吗？其下的回复也会被删除。'
        : '确定要删除这条评论吗？';
      this.$confirm(warnMsg, '提示', { type: 'warning' }).then(() => {
        deleteComment(comment.id).then(resp => {
          if (resp && resp.code === 200) {
            this.$message.success('删除成功');
            this.fetchComments();
            if (this.currentPost) {
              this.currentPost.commentCount = Math.max(0, (this.currentPost.commentCount || 1) - (descendantCount + 1));
            }
          }
        }).catch(() => {
          this.$message.error('删除失败');
        });
      }).catch(() => {});
    }
  }
};
</script>

<style scoped>
.forum-container {
  max-width: 1200px;
  margin: 0 auto;
}

.forum-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.forum-title {
  margin: 0;
  font-size: 22px;
  color: #303133;
}

.forum-search {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.search-input {
  flex: 1;
  min-width: 240px;
}

.filter-select {
  width: 140px;
}

.dept-select {
  width: 200px;
}

.forum-stats {
  margin-bottom: 16px;
  font-size: 14px;
  color: #909399;
}

.forum-stats strong {
  color: #409EFF;
  margin: 0 4px;
}

.post-list {
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: calc(100vh - 350px);
  overflow: auto;
}

.post-card {
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 10px;
  padding: 18px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.post-card:hover {
  border-color: #409EFF;
  box-shadow: 0 2px 12px rgba(64, 158, 255, 0.12);
  transform: translateY(-1px);
}

.post-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.post-status-tag {
  flex-shrink: 0;
}

.post-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}

.post-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.post-deadline {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #909399;
}

.post-preview {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
}

.post-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.post-comment-count {
  font-size: 13px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
}

.post-actions {
  display: flex;
  gap: 4px;
}

.ml-2 {
  margin-left: 8px;
}

.forum-pagination {
  margin-top: 24px;
  text-align: center;
}

.post-detail-dialog ::v-deep .el-dialog__body {
  min-height: 78vh;
  overflow-y: auto;
  padding: 20px 24px;
}

.detail-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.detail-deadline {
  font-size: 13px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
}

.detail-content {
  font-size: 15px;
  line-height: 1.8;
  color: #303133;
  white-space: pre-wrap;
  word-break: break-word;
}

.comment-section {
  margin-top: 8px;
}

.comment-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  margin-bottom: 16px;
  color: #303133;
}

.comment-list {
  height: 430px;
  overflow-y: auto;
  margin-bottom: 16px;
}

.comment-item {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;
}

.comment-item:last-child {
  border-bottom: none;
}

.comment-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409EFF, #66b1ff);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  flex-shrink: 0;
}

.comment-avatar.small {
  width: 30px;
  height: 30px;
  font-size: 12px;
}

.comment-body {
  flex: 1;
  min-width: 0;
}

.comment-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.comment-username {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.comment-time {
  font-size: 12px;
  color: #c0c4cc;
}

.comment-content {
  margin: 0;
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.comment-actions {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-icon-thumb {
  width: 20px;
  height: 20px;
  display: inline-block;
  vertical-align: middle;
}

.action-icon-thumb.flip {
  transform: rotate(180deg);
}

.like-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  color: #909399;
  transition: color 0.15s;
  user-select: none;
}

.like-btn:hover {
  color: #409EFF;
}

.like-btn.liked {
  color: #409EFF;
}

.dislike-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  color: #909399;
  transition: color 0.15s;
  user-select: none;
}

.dislike-btn:hover {
  color: #E6A23C;
}

.dislike-btn.disliked {
  color: #E6A23C;
}

.like-count,
.dislike-count {
  font-size: 12px;
}

.reply-input-box {
  margin-top: 10px;
  padding: 10px 12px;
  background: #fafbfc;
  border: 1px solid #e8eaed;
  border-radius: 8px;
}

.reply-input-btns {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.nested-replies {
  margin-top: 10px;
  margin-left: 16px;
  padding: 8px 0 6px 0;
  padding-left: 12px;
  background: #f7f8fa;
  border-radius: 8px;
  border-left: 2px solid #e8eaed;
}

.mention-textarea-wrapper {
  position: relative;
  width: 100%;
}

.comment-input-area {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.comment-submit {
  align-self: flex-end;
}

.comment-actions ::v-deep .el-button {
  font-size: 12px;
  height: auto;
  padding: 2px 8px;
  color: #409EFF;
  border-radius: 4px;
  transition: all 0.2s ease;
  background: transparent;
}
.comment-actions ::v-deep .el-button:hover {
  color: #66b1ff;
  background: rgba(64, 158, 255, 0.08);
}

.comment-actions ::v-deep .el-button--danger {
  color: #f56c6c;
}

.comment-actions ::v-deep .el-button--danger:hover {
  color: #f78989;
  background: rgba(245, 108, 108, 0.08);
}

/* ===== Tab 栏 ===== */
.forum-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.forum-header-left {
  display: flex;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
}
.forum-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  border-bottom: 1px solid #f0f2f5;
}
.forum-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color 0.2s, border-color 0.2s;
}
.forum-tab:hover { color: #409eff; }
.forum-tab.active {
  color: #409eff;
  border-bottom-color: #409eff;
  font-weight: 600;
}
.tab-count {
  font-size: 12px;
  background: #eef0f4;
  color: #606266;
  padding: 1px 8px;
  border-radius: 10px;
}
.forum-tab.active .tab-count {
  background: #e6f2ff;
  color: #409eff;
}

/* ===== 图片网格 ===== */
.forum-image-wrapper { padding: 4px 0 20px; }
.image-grid {
  display: grid;
  /* 固定 4 列, 避免大屏下意外铺太宽; <1200px 自动降为 3 列, <768 降 2 列 */
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
@media (max-width: 1200px) {
  .image-grid { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 768px) {
  .image-grid { grid-template-columns: repeat(2, 1fr); }
}
.image-card {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  background: #f5f7fa;
  cursor: pointer;
  transition: transform 0.25s, box-shadow 0.25s;
  box-shadow: 0 1px 4px rgba(31, 45, 61, 0.05);
}
.image-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 22px rgba(31, 45, 61, 0.12);
}
.image-thumb {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  animation: image-fade-in 0.3s ease;
}
@keyframes image-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* 加载中骨架 */
.image-card-skeleton {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(90deg, #f0f2f5 25%, #e6e9ed 50%, #f0f2f5 75%);
  background-size: 200% 100%;
  animation: image-skeleton-shine 1.2s infinite linear;
  color: #c0c4cc;
  font-size: 32px;
}
@keyframes image-skeleton-shine {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

/* 加载失败 */
.image-card-error {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: #fafafa;
  color: #909399;
  font-size: 12px;
}
.image-card-error i { font-size: 30px; color: #c0c4cc; }
.retry-btn {
  margin-top: 2px;
  font-size: 12px;
  padding: 0;
}
.image-card-mask {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.55));
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 12px;
}
.image-card:hover .image-card-mask { opacity: 1; }
.image-card-del {
  background: rgba(245, 108, 108, 0.9) !important;
  border: none !important;
}
.image-card-del:hover { background: #f56c6c !important; }
.image-card-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  aspect-ratio: 4 / 3;
  border: 2px dashed #dcdfe6;
  background: #fafbfc;
  color: #909399;
  gap: 8px;
}
.image-card-add:hover {
  border-color: #409eff;
  color: #409eff;
  transform: translateY(-3px);
}
.image-card-add i { font-size: 30px; }

/* ===== 添加图片弹窗 ===== */
.image-uploader .upload-trigger {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 130px;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  background: #fafbfc;
  color: #909399;
  cursor: pointer;
  gap: 8px;
  font-size: 13px;
}
.image-uploader .upload-trigger:hover {
  border-color: #409eff;
  color: #409eff;
}
.image-uploader .upload-trigger i { font-size: 24px; }
.upload-preview {
  position: relative;
  width: 200px;
  height: 130px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
}
.upload-preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.upload-preview-mask {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.45);
  color: #fff;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}
.upload-preview:hover .upload-preview-mask { opacity: 1; }
.upload-tip {
  font-size: 12px;
  color: #b0b6c0;
  margin-top: 8px;
}

.ratio-row { margin-bottom: 8px; }
.ratio-preview {
  width: 200px;
  max-width: 100%;
  background: #fafbfc;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  overflow: hidden;
}
.ratio-preview-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ===== 大图预览 ===== */
.image-preview-wrap {
  width: 100%;
  background: #1f2d3d;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.image-preview-img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}
</style>